import { HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, switchMap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { getClientOS } from '../utils/generic';
import { authenticationSessionKey, SessionRefreshService } from '../../core/services/session-refresh.service';

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  if (req.url !== environment.apiEndpoint && !req.url.startsWith(environment.apiEndpoint + '/')) return next(req);
  const refresh = inject(SessionRefreshService);
  const path = authPath(req);
  // Include explicit refreshes from app startup, foreground and streaming in the same operation.
  if (req.method === 'POST' && path === '/auth/refreshToken') return refresh.refresh();
  const publicRequest = isPublicAuthRequest(req);
  const token = localStorage.getItem('ACCESS_TOKEN');
  const session = authenticationSessionKey();
  return next(addHeaders(req, token)).pipe(catchError((error: HttpErrorResponse) => {
    if (error.status !== 401 || publicRequest || !token) return throwError(() => error);
    if (authenticationSessionKey() !== session) return throwError(() => error);
    const currentToken = localStorage.getItem('ACCESS_TOKEN');
    if (!currentToken) return throwError(() => error);
    // A slow response can report expiry of a token that has already been renewed.
    if (currentToken !== token) return next(addHeaders(req, currentToken));
    return refresh.refresh().pipe(switchMap(response => {
      if (authenticationSessionKey() !== session) return throwError(() => error);
      return next(addHeaders(req, response.body!.access_token));
    }));
  }));
};

function isPublicAuthRequest(request: HttpRequest<unknown>): boolean {
  return ['/auth', '/auth/refreshToken', '/auth/request', '/auth/ping'].includes(authPath(request));
}

function authPath(request: HttpRequest<unknown>): string {
  return request.url.slice(environment.apiEndpoint.length).split('?')[0].replace(/\/$/, '');
}

function addHeaders(request: HttpRequest<unknown>, token: string | null): HttpRequest<unknown> {
  let headers = request.headers;
  if (!(request.body instanceof FormData) && !headers.has('Content-Type')) headers = headers.set('Content-Type', 'application/json');
  headers = headers.set('X-App-Version', environment.appVersion).set('X-Device-OS', getClientOS());
  if (token && !isPublicAuthRequest(request)) headers = headers.set('Authorization', 'Bearer ' + token);
  return request.clone({ headers });
}
